import { authClient } from "@/api/auth-client";
import { env } from "@/env";
import { redirect } from "@tanstack/react-router";
import {
  type Permission,
  type UserRole,
  asUserRole,
  getPermissionsForRoles,
} from "@template/shared";

/**
 * Route-level guard for TanStack Router `beforeLoad`. Fetches the
 * caller's profile via the tRPC `auth.getSession` endpoint and
 * redirects them away if they lack the required permission.
 *
 * Permission is checked against the UNION of the primary `role` and any
 * supplemental `roles`, matching the server-side gate. Unauthenticated users
 * and evidence-only accounts (`appAccess === false`) get sent to /login;
 * authenticated users without the permission get sent to /dashboard.
 */
export function requirePermissionGuard(permission: Permission) {
  return async () => {
    const session = await authClient.getSession();
    if (!session.data?.session) {
      throw redirect({ to: "/login" });
    }

    // Fetch profile via the tRPC getSession endpoint.
    const res = await fetch(`${env.VITE_API_URL}/api/trpc/auth.getSession`, {
      credentials: "include",
    });
    if (!res.ok) {
      throw redirect({ to: "/dashboard" });
    }
    const body = (await res.json()) as {
      result?: {
        data?: { profile?: { role?: unknown; roles?: unknown; appAccess?: unknown } | null };
      };
    };
    const profile = body.result?.data?.profile;

    // Evidence-only accounts must not reach app routes even with a cookie.
    if (profile?.appAccess === false) {
      throw redirect({ to: "/login" });
    }

    const primary = asUserRole(profile?.role);
    if (!primary) {
      throw redirect({ to: "/dashboard" });
    }
    const extra = Array.isArray(profile?.roles)
      ? profile.roles.map((r) => asUserRole(r)).filter((r): r is UserRole => r !== null)
      : [];
    const roles = Array.from(new Set<UserRole>([primary, ...extra]));

    if (!getPermissionsForRoles(roles).has(permission)) {
      throw redirect({ to: "/dashboard" });
    }
    return { role: primary };
  };
}
